import { Nullable } from 'src/core/common/domain/types/types';

export type AttendanceConfigurationPrimitives = {
  mode: Nullable<AttendanceMode>;
  // Optional: configurations stored by app versions older than the method do not carry it.
  method?: Nullable<AttendanceMethod>;
};

export const AttendanceModes = {
  BASIC: 'basic',
  AUTH_AND_BASIC: 'auth_and_basic',
} as const;
export type AttendanceMode = typeof AttendanceModes[keyof typeof AttendanceModes];

export const AttendanceMethods = {
  MANUAL: 'manual',
  NFC_IN_DS: 'nfc_in_ds',
} as const;
export type AttendanceMethod = typeof AttendanceMethods[keyof typeof AttendanceMethods];

export class AttendanceConfiguration {
  constructor(public readonly mode: Nullable<AttendanceMode>, public readonly method: AttendanceMethod) {}

  static fromPrimitives(primitives: AttendanceConfigurationPrimitives): AttendanceConfiguration {
    if (!primitives) {
      return new this(null, AttendanceMethods.MANUAL);
    }

    const mode = primitives?.mode ?? '';
    if (mode.trim() === '') {
      return new this(null, AttendanceMethods.MANUAL);
    }

    return new this(mode as AttendanceMode, AttendanceConfiguration.methodFrom(primitives.method));
  }

  // The API omits the method, or sends null, when the punch is manual. A method this version does not know
  // falls back to manual too, so the user can still punch.
  private static methodFrom(method: Nullable<string> | undefined): AttendanceMethod {
    const knownMethods: Array<string> = Object.values(AttendanceMethods);
    if (!knownMethods.includes(method ?? '')) {
      return AttendanceMethods.MANUAL;
    }

    return method as AttendanceMethod;
  }

  toPrimitives(): AttendanceConfigurationPrimitives {
    return {
      mode: this.mode,
      method: this.method,
    };
  }

  isEnabled(): boolean {
    return this.mode !== null;
  }

  isBasic(): boolean {
    return this.mode === AttendanceModes.BASIC;
  }

  isAuthAndBasic(): boolean {
    return this.mode === AttendanceModes.AUTH_AND_BASIC;
  }

  punchesWithNfcTag(): boolean {
    return this.method === AttendanceMethods.NFC_IN_DS;
  }
}
